/**
 * Stale lazy-chunk recovery.
 *
 * Every page is `React.lazy(() => import('./pages/X'))`, so its JS is
 * fetched on first navigation. Each deploy rebuilds the app with new
 * content-hashed chunk filenames and replaces the served bundle, so a
 * browser tab opened *before* a deploy is still holding the old
 * index.html that points at chunk names which no longer exist. The
 * next time that tab navigates to a not-yet-loaded route, the dynamic
 * import() fails.
 *
 * Browsers phrase that failure differently:
 *   - Safari:  "Importing a module script failed."
 *   - Chrome:  "Failed to fetch dynamically imported module: <url>"
 *   - Firefox: "error loading dynamically imported module"
 *   - Vite CSS preload: "Unable to preload CSS for <url>"
 * Webpack-era builds raised a named ChunkLoadError; kept for safety.
 *
 * The fix is to hard-reload once, which pulls the fresh (no-cache)
 * index.html and its new chunk map. This is normal recovery, not a
 * crash, so the boundary suppresses it from Sentry (otherwise every
 * deploy floods the project with this noise — the issue that prompted
 * this code on 2026-06-15).
 */

const RELOAD_KEY = 'sp-chunk-reloaded-at';
// If a reload doesn't fix it within this window, the chunk is genuinely
// broken (not merely stale) — stop reloading and let the UI fall back,
// so we never trap the user in a reload loop.
const RELOAD_COOLDOWN_MS = 10_000;

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const err = error as { name?: string; message?: string };
  if (err.name === 'ChunkLoadError') return true;
  const msg = err.message || '';
  return (
    /importing a module script failed/i.test(msg) ||
    /failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /unable to preload css/i.test(msg) ||
    // Safari/Firefox can surface a failed React.lazy import as a
    // TypeError from React's lazyInitializer instead of an import
    // failure — Safari: "undefined is not an object (evaluating
    // 'T._result.default')", Firefox: "T._result is undefined".
    // `_result` is React.lazy's internal payload slot, so this token
    // can't match an ordinary application TypeError (which must stay
    // unmatched — auto-reloading on a real bug would mask it).
    // Sentry JAVASCRIPT-REACT-2, 2026-06-30/07-02: two Safari users got
    // the error panel instead of the silent one-shot reload.
    /\._result\b/.test(msg)
  );
}

/**
 * Hard-reload once to recover from a stale chunk. The sessionStorage
 * cooldown survives the reload and prevents an infinite loop when the
 * chunk is genuinely missing rather than just out of date. Returns true
 * if a reload was triggered, false if suppressed by the cooldown or if
 * the guard storage is unavailable (in which case we do NOT reload, to
 * stay loop-safe).
 */
export function reloadOnceForStaleChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Storage blocked (private mode / sandboxed iframe). Without a
    // cross-reload guard we can't prove we won't loop, so don't reload.
    return false;
  }
  window.location.reload();
  return true;
}
